import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../../components/ui/table';
import dayjs from 'dayjs';
import { History, useHistoryStore } from '../../store/useHistoryStore';
import { Button, Label, Modal, Popover, TextInput, Textarea } from 'flowbite-react';
import { HiOutlineExclamationCircle } from 'react-icons/hi';
import toast from 'react-hot-toast';
import { useCollegeStore } from '../../store/useCollegeStore';
import { useCanAccessResource, useHasPermission, useIsMainAdmin } from '../../utils/permissions';
import { usePermissionStore } from '../../store/usePermissionStore';
import AccessDenied from '../../components/common/AccessDenied';

export default function HistoryPage() {
  const navigate = useNavigate();
  const getHistories = useHistoryStore((state) => state.getHistories);
  const deleteHistory = useHistoryStore((state) => state.deleteHistory);
  const histories = useHistoryStore((state) => state.histories);
  const createHistory = useHistoryStore((state) => state.createHistory);
  const updateHistory = useHistoryStore((state) => state.updateHistory);
  const loading = useHistoryStore((state) => state.loading);
  const pagination = useHistoryStore((state) => state.pagination);
  const uploadFile = useCollegeStore((state) => state.uploadFile);
  const uploadedFile = useCollegeStore((state) => state.uploadedFile);

  const canAccess = useCanAccessResource('history');
  const hasReadPermission = useHasPermission('history', 'read');
  const hasCreatePermission = useHasPermission('history', 'create');
  const hasUpdatePermission = useHasPermission('history', 'update');
  const hasDeletePermission = useHasPermission('history', 'delete');
  const isMainAdmin = useIsMainAdmin();
  const isLoadingPermissions = usePermissionStore((state) => state.isLoading);
  const hasFetched = usePermissionStore((state) => state.hasFetched);

  const [tableData, setTableData] = useState<History[]>([]);
  const [search, setSearch] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [openModal, setOpenModal] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<string | null>(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [modalType, setModalType] = useState<string>('add');
  const [editId, setEditId] = useState<string | null>(null);

  const [period, setPeriod] = useState('');
  const [priest, setPriest] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');
  const [order, setOrder] = useState<number>(1);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    if (hasFetched && !isLoadingPermissions && (hasReadPermission || isMainAdmin)) {
      getHistories(undefined, search || undefined, currentPage, limit);
    }
  }, [getHistories, search, currentPage, limit, hasReadPermission, isMainAdmin, hasFetched, isLoadingPermissions]);

  useEffect(() => {
    setTableData(histories);
  }, [histories]);

  const handleAddOrEdit = async () => {
    if (!period || !priest) {
      toast.error('Please fill in all required fields.', {
        duration: 2000,
        icon: '❌',
      });
      return;
    }

    // Validate description length (max 150 characters)
    if (description.length > 150) {
      toast.error('Description cannot exceed 150 characters.', {
        duration: 2000,
        icon: '❌',
      });
      return;
    }

    if (modalType === 'add') {
      await createHistory({ period, priest, description, image: image || '', order });
    } else {
      if (!editId) return;
      await updateHistory(editId, { period, priest, description, image: image || '', order });
    }

    setOpenAddModal(false);
    setPeriod('');
    setPriest('');
    setDescription('');
    setImage('');
    setOrder(1);
    setEditId(null);
    setModalType('add');
    setIsUploading(false);
    useCollegeStore.setState({ uploadedFile: null });
  };

  const handleEdit = (history: History) => {
    setEditId(history._id);
    setModalType('edit');
    setOpenAddModal(true);
    setPeriod(history.period);
    setPriest(history.priest);
    setDescription(history.description || '');
    setImage(history.image || '');
    setOrder(history.order);
  };

  const handleDelete = () => {
    if (selectedHistory !== null) {
      deleteHistory(selectedHistory);
      setOpenModal(false);
      setSelectedHistory(null);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Check file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        toast.error('File size exceeds the maximum limit of 5MB. Please upload a smaller file.', {
          duration: 3000,
          icon: '❌',
        });
        e.target.value = ''; // Reset file input
        return;
      }

      setIsUploading(true);
      try {
        await uploadFile(file);
      } catch (error) {
        // Error will be handled by displayErrorToast in the API function
      } finally {
        setIsUploading(false);
      }
    }
  };

  useEffect(() => {
    if (uploadedFile?.data?.upload) {
      const upload = uploadedFile.data.upload as any;
      // Handle different response formats (ImageKit returns url, S3 returns Location, old format has uploadUrl)
      if (upload.url) {
        setImage(upload.url);
      } else if (upload.Location) {
        setImage(upload.Location);
      } else if (upload.uploadUrl) {
        setImage(upload.uploadUrl);
      }
    }
  }, [uploadedFile]);

  // Show loading while permissions are being fetched
  if (isLoadingPermissions || !hasFetched) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-lg text-gray-600">Loading permissions...</div>
        </div>
      </div>
    );
  }

  // Show permission denied message if no access
  if (!canAccess && !isMainAdmin) {
    return <AccessDenied resourceName="History" />;
  }

  return (
    <>
      <Modal
        show={openModal}
        size="md"
        position="center"
        onClose={() => {
          setOpenModal(false);
          setSelectedHistory(null);
        }}
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
              Are you sure you want to delete this history entry?
            </h3>
            <div className="flex justify-center gap-4">
              <Button
                onClick={handleDelete}
                className="px-6 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                {"Yes, I'm sure"}
              </Button>
              <Button
                onClick={() => {
                  setOpenModal(false);
                  setSelectedHistory(null);
                }}
                className="px-6 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                No, cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={openAddModal}
        size="lg"
        popup
        onClose={() => {
          setOpenAddModal(false);
          setPeriod('');
          setPriest('');
          setDescription('');
          setImage('');
          setOrder(1);
          setEditId(null);
          setModalType('add');
          setIsUploading(false);
          useCollegeStore.setState({ uploadedFile: null });
        }}
      >
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-6">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              {modalType === 'add' ? 'Add History Entry' : 'Edit History Entry'}
            </h3>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="period" value="Period *" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <TextInput
                id="period"
                type="text"
                placeholder="e.g., 1985-1990"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="priest" value="Priest Name *" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <TextInput
                id="priest"
                type="text"
                placeholder="e.g., Fr. Patrick O'Brien"
                value={priest}
                onChange={(e) => setPriest(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>

            <div>
              <div className="mb-2 flex justify-between items-center">
                <Label htmlFor="description" value="Description (Optional - Max 150 characters)" style={{ fontFamily: 'Lato, sans-serif' }} />
                <span
                  className={`text-sm font-medium ${
                    description.length > 150
                      ? 'text-red-500'
                      : description.length === 150
                      ? 'text-orange-500'
                      : description.length >= 140
                      ? 'text-yellow-600'
                      : 'text-gray-500'
                  }`}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  {description.length}/150
                </span>
              </div>
              <Textarea
                id="description"
                placeholder="Enter description (max 150 characters)..."
                value={description}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 150) {
                    setDescription(value);
                  } else {
                    toast.error('Description cannot exceed 150 characters.', {
                      duration: 2000,
                      icon: '❌',
                    });
                  }
                }}
                rows={4}
                maxLength={150}
                className={
                  description.length > 150
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : description.length === 150
                    ? 'border-orange-300 focus:border-orange-300'
                    : description.length >= 140
                    ? 'border-yellow-300'
                    : ''
                }
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
              {description.length > 150 && (
                <p className="mt-1 text-sm text-red-500 font-medium" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Description cannot exceed 150 characters. Please reduce the length.
                </p>
              )}
              {description.length === 150 && (
                <p className="mt-1 text-sm text-orange-500 font-medium" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Maximum character limit reached (150 characters).
                </p>
              )}
              {description.length >= 140 && description.length < 150 && (
                <p className="mt-1 text-sm text-yellow-600" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {150 - description.length} characters remaining.
                </p>
              )}
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="image" value="Image (Optional)" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              {isUploading ? (
                <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B8860B] mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
                    Uploading image...
                  </p>
                </div>
              ) : image ? (
                <div className="mb-2">
                  <img src={image} alt="History" className="w-full h-48 object-cover rounded-lg" />
                  <Button color="failure" size="sm" onClick={() => setImage('')} className="mt-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                    Remove Image
                  </Button>
                </div>
              ) : (
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={isUploading}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#B8860B] file:text-white hover:file:bg-[#9A7209] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              )}
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="order" value="Order" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <TextInput
                id="order"
                type="number"
                placeholder="Display order (starts from 1)"
                value={order}
                onChange={(e) => setOrder(parseInt(e.target.value) || 1)}
                min={1}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>

            <div className="w-full">
              <Button
                onClick={handleAddOrEdit}
                disabled={loading || isUploading || !period || !priest || description.length > 150}
                className="w-full px-6 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                {loading ? 'Processing...' : isUploading ? 'Uploading...' : modalType === 'add' ? 'Add History Entry' : 'Edit History Entry'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <PageMeta title="History" description="Manage church history" />
      <PageBreadcrumb pageTitle="History" />
      <div className="space-y-6">
        <ComponentCard title="History">
          <div className="flex flex-col md:flex-row w-full md:justify-start md:items-center gap-5">
            <Button
              onClick={() => {
                setOpenAddModal(true);
                setModalType('add');
                setOrder(1);
                setPeriod('');
                setPriest('');
                setDescription('');
                setImage('');
              }}
              className="px-6 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium flex items-center justify-center"
              style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
            >
              Add History Entry
            </Button>
            <div className="relative">
              <span className="absolute -translate-y-1/2 pointer-events-none left-4 top-1/2">
                <svg
                  className="fill-gray-500 dark:fill-gray-400"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                    fill=""
                  />
                </svg>
              </span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                type="text"
                placeholder="Search history..."
                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-14 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-[#B8860B] focus:outline-none focus:ring focus:ring-[#B8860B]/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-[#B8860B] xl:w-[430px]"
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] mt-6">
            <div className="max-w-full overflow-x-auto">
              <div className="min-w-[1000px]">
                <Table>
                  <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                    <TableRow>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Image
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Period
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Priest
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Order
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Created At
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHeader>

                  <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                    {tableData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="px-5 py-8 text-center text-gray-500 dark:text-gray-400"
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        >
                          {loading ? 'Loading...' : 'No history entries found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableData.map((history) => (
                        <TableRow key={history._id}>
                          <div
                            className="contents cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-md transition-all duration-200 group"
                            onClick={() => navigate(`/history/${history._id}`)}
                          >
                            <TableCell className="px-5 py-4 sm:px-6 text-start group-hover:border-l-4 group-hover:border-[#B8860B] transition-all">
                              {history.image ? (
                                <img src={history.image} alt={`${history.period}`} className="w-24 h-16 object-cover rounded" />
                              ) : (
                                <div className="w-24 h-16 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center">
                                  <span className="text-gray-400 text-xs">No Image</span>
                                </div>
                              )}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {history.period}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {history.priest}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {history.order}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {dayjs(history.createdAt).format('DD MMM YYYY')}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                              <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                                <Popover
                                  placement="top"
                                  aria-labelledby="default-popover"
                                  content={
                                    <div className="py-2 px-3">
                                      <div
                                        className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 cursor-pointer"
                                        onClick={() => {
                                          setSelectedHistory(history._id);
                                          setOpenModal(true);
                                        }}
                                        style={{ fontFamily: 'Lato, sans-serif' }}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="lucide lucide-trash-2"
                                        >
                                          <path d="M3 6h18" />
                                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                          <line x1="10" x2="10" y1="11" y2="17" />
                                          <line x1="14" x2="14" y1="11" y2="17" />
                                        </svg>
                                        Delete
                                      </div>

                                      <div
                                        className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 cursor-pointer"
                                        onClick={() => handleEdit(history)}
                                        style={{ fontFamily: 'Lato, sans-serif' }}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="lucide lucide-pencil"
                                        >
                                          <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5 5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                                          <path d="m15 5 4 4" />
                                        </svg>
                                        Edit
                                      </div>
                                    </div>
                                  }
                                >
                                  <button
                                    className="flex items-center justify-center w-10 h-10 text-gray-700 rounded-lg z-99999 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors"
                                    style={{ fontFamily: 'Lato, sans-serif' }}
                                  >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M5.99902 10.4951C6.82745 10.4951 7.49902 11.1667 7.49902 11.9951V12.0051C7.49902 12.8335 6.82745 13.5051 5.99902 13.5051C5.1706 13.5051 4.49902 12.8335 4.49902 12.0051V11.9951C4.49902 11.1667 5.1706 10.4951 5.99902 10.4951ZM17.999 10.4951C18.8275 10.4951 19.499 11.1667 19.499 11.9951V12.0051C19.499 12.8335 18.8275 13.5051 17.999 13.5051C17.1706 13.5051 16.499 12.8335 16.499 12.0051V11.9951C16.499 11.1667 17.1706 10.4951 17.999 10.4951ZM13.499 11.9951C13.499 11.1667 12.8275 10.4951 11.999 10.4951C11.1706 10.4951 10.499 11.1667 10.499 11.9951V12.0051C10.499 12.8335 11.1706 13.5051 11.999 13.5051C12.8275 13.5051 13.499 12.8335 13.499 12.0051V11.9951Z"
                                        fill="currentColor"
                                      />
                                    </svg>
                                  </button>
                                </Popover>
                              </div>
                            </TableCell>
                          </div>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 border-t border-gray-200 dark:border-white/[0.05]">
              <div className="text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
                Showing {(pagination.page - 1) * pagination.limit + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={!pagination.hasPrevPage || loading}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        color={pagination.page === pageNum ? 'warning' : 'light'}
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={loading}
                        style={{ fontFamily: 'Lato, sans-serif', minWidth: '40px' }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={!pagination.hasNextPage || loading}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </ComponentCard>
      </div>
    </>
  );
}
